import {
    AfterContentChecked,
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Inject,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    Type
} from "@angular/core";
import {Location} from "@angular/common";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {
    AuthService,
    ConfigurationService,
    DisplayableDataServerMessage,
    EntityData,
    EntityModel,
    LocalisableEntityData,
    ValidationMessage,
    ValidationService
} from "@fedeltapointofsale/data-client";
import {EditService} from "./edit.service";
import {EntityFormBuilder} from "../entity-form-builder/entity-form-builder";
import {
    FUIAlertActions,
    FUIAlertService,
    FUIAlertType,
    FUIBreakpoints,
    FUIFormationService,
    FUIFormationType,
    FUIModalsService,
    FUIResponsiveService,
    FUISpinnersService,
    FUIStackedPageContextService,
    FUIStackedPageService
} from "@fedeltapointofsale/ui";
import {TranslateService} from "@ngx-translate/core";
import {ErrorHandlerService} from "../../../../error-handler.service";
import {EditParams} from "./edit-params";
import {EditMode} from "./edit-mode";
import {Config} from "../../../../config";
import {AbstractDataPageComponent} from "../../abstract-data-page.component";
import {AnalyticsService} from "../../../../analytics/analytics.service";
import {AnalyticEvent} from "../../../../analytics/analytic-event";
import {
    CustomerSupportMessagingService
} from "../../../../customer-support-messaging/customer-support-messaging.service";
import {firstValueFrom, Observable, Subscription} from "rxjs";
import {ReportGenerationService} from "../../report/report-generation.service";
import {EditPageActionEvent} from "./edit-page-action.event";
import {EntityActionsComponent} from "../../actions/entity-actions.component";
import {EntityActionComponent} from "../../actions/entity-action.component";
import {EditPageUtilityControlsComponent} from "./edit-page-utility-controls/edit-page-utility-controls.component";
import {EditPageSidePanel} from "./edit-page-side-panel/edit-page-side-panel.component";
import * as FileSaver from "file-saver";
import {FileUploadComponent} from "../../../../shared/file/file-upload.component";
import {SearchFormExtendedService} from "../../search/search-form/search-form-extended.service";
import {RoutingDiscoveryService} from "../../../../routing-discovery.service";

@Component({
    selector: "wbo-edit-page",
    templateUrl: "./edit-page.component.html",
    styleUrls: ["edit-page.component.scss"],
    providers: [
        EditService,
        FUIFormationService,
        ReportGenerationService,
        SearchFormExtendedService
    ]
})
export class EditPageComponent extends AbstractDataPageComponent implements OnInit, AfterContentChecked, OnDestroy, AfterContentInit {
    public static readonly NEW_ENTITY_PARAM = "new";
    public static readonly EXIT_ROUTE_BACK = "BACK";
    public static readonly EXIT_ROUTE_STAY = "STAY";

    FUIFormationType = FUIFormationType;

    @Input() description: string;

    /**
     * Event emitted before the entity is saved
     * @type {EventEmitter<any>}
     */
    @Output() saving: EventEmitter<EditPageActionEvent> = new EventEmitter<EditPageActionEvent>();

    /**
     * Event emitted before the cancel action is performed
     * @type {EventEmitter<any>}
     */
    @Output() cancelling: EventEmitter<EditPageActionEvent> = new EventEmitter<EditPageActionEvent>();

    /**
     * Event emitted when adding another entity. The event content is the previous instance and can be used to copy values to the new entity that is already in the EntityModel
     */
    @Output() addingAnother: EventEmitter<any> = new EventEmitter();

    /**
     * Event emitted when an entity has been successfully saved
     */
    @Output() saved: EventEmitter<EditPageActionEvent> = new EventEmitter<EditPageActionEvent>();

    /**
     * Event emitted after a entity is imported
     */
    @Output() entityImported: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Event emitted when an entity failed to save
     */
    @Output() saveFailed: EventEmitter<EditPageActionEvent> = new EventEmitter<EditPageActionEvent>();

    /**
     * Disable showing the outlet selection
     */
    @Input() disableEntityLocalisation: boolean;

    @Input() formationType: FUIFormationType = FUIFormationType.NON_COLLAPSIBLE;

    /**
     * Enable import and export of the current entity
     */
    @Input() enableImportExport: boolean;

    @Input() ignoreFieldsImportExport: string[];

    @Input() shouldDiscardChanges: boolean = false;

    @Input() stayOnCreate: boolean = false;

    @ContentChildren(EntityActionsComponent) entityActionsComponent: QueryList<EntityActionsComponent>;

    @ContentChildren(EditPageUtilityControlsComponent) public utilityControls: QueryList<EditPageUtilityControlsComponent>;
    @ContentChild(EditPageSidePanel) public sidePanel: EditPageSidePanel;

    /**
     * Removes the main save button and actions buttons
     */
    @Input() disableAction: boolean;

    /**
     * Removes the actions buttons
     */
    @Input() disabledEditActions: boolean;

    @Input() utilityPlaceholder: string;

    @Input() forceShowUtilityPlaceholder: boolean;

    /**
     * Removes the main save button and actions buttons
     */
    @Input() hideClone: boolean;

    @Input() hideCancelAction: boolean;

    @Input() hideSaveAddAnotherAction: boolean;

    @Input() hideSaveAction: boolean;

    @Input() disableSave: boolean = false;

    @Input() addAnotherOnSave: boolean = false;

    /**
     * Override the exit route
     */
    @Input() exitRoute: string;

    @Input() maskSubject: boolean;

    @Input() scrollSubject: boolean = true;

    @Input() cancelButtonTitle: string;

    /**
     * The loaded identifier when editing (typically derived, unless forceEditMode is specified)
     */
    @Input() loadedIdentifier: string;

    @Input() overrideShowSaveButton: boolean;

    EditMode = EditMode;

    canCreate: boolean;

    forceEditMode: EditMode;

    /**
     * Indicates that the data has loaded
     */
    initialised: boolean;

    /**
     * Cloning indentifer extracted from the route query params
     */
    cloningIdentifier: string;

    saveLabel: string;

    isAddingAnother: boolean;

    /**
     * Indicates that the user will be kept on the same page after saving
     */
    isSaveAndStay: boolean;

    /**
     * Indicates that after saving the entity it will call the clone action
     */
    savingAndCloning: boolean;

    /**
     * Indicates of short button labels should be used. This is required for small screen sizes.
     */
    shortButtonLabels: boolean;

    /**
     * Indicates if the Add Another button should be hidden
     */
    hideAddAnotherButton: boolean;

    entityActions: EntityActionComponent[] = [];
    private entityActionsSubscription: Subscription;

    private _saveButtonTitle: string;

    private savedData: EntityModel<any>;

    constructor(public entityModel: EntityModel<any>,
                validationService: ValidationService,
                editService: EditService,
                changeDetectorRef: ChangeDetectorRef,
                entityFormBuilder: EntityFormBuilder,
                formationService: FUIFormationService,
                alertService: FUIAlertService,
                translate: TranslateService,
                errorHandlerService: ErrorHandlerService,
                spinnersService: FUISpinnersService,
                private route: ActivatedRoute,
                private authService: AuthService,
                private router: Router,
                private location: Location,
                private configurationService: ConfigurationService,
                @Optional() stackedPageContextService: FUIStackedPageContextService,
                @Inject(ValidationService.APPLICATION_KEY) application: string,
                elementRef: ElementRef,
                public customerSupportMessagingService: CustomerSupportMessagingService,
                private analyticsService: AnalyticsService,
                private responsiveService: FUIResponsiveService,
                public reportGenerationService: ReportGenerationService,
                private stackedPageService: FUIStackedPageService,
                private routingDiscoveryService: RoutingDiscoveryService,
                private modalsService: FUIModalsService,
                private injector: Injector) {
        super(entityModel, validationService, editService, changeDetectorRef, entityFormBuilder, formationService, alertService, translate, spinnersService,
            errorHandlerService, stackedPageContextService, application, elementRef);

        this.configureForScreenSize();
        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(() => this.configureForScreenSize()));
        this.initialiseMode();
    }

    @Input() set editMode(editMode: EditMode) {
        this.forceEditMode = editMode;
    }

    get editMode(): EditMode {
        return this.editService.editMode;
    }

    get editMode$(): Promise<EditMode> {
        return firstValueFrom(this.editService.editMode$);
    }

    get isAddEditMode(): boolean {
        return this.editService.editMode === EditMode.ADD;
    }

    get isStackedPageMode(): boolean {
        return this.stackedPageContextService != undefined;
    }

    get showCloneAction(): boolean {
        return this.editMode === EditMode.EDIT &&
            !this.disableAction &&
            !this.hideClone &&
            this.canCreate &&
            this.entityModel.isEntityCloneable() &&
            (!this.stackedPageContextService || (EditParams.isInstance(this.stackedPageContextService.params) && this.stackedPageContextService.params.allowClone));
    }

    get showEditActions(): boolean {
        return !this.disabledEditActions && (this.showCloneAction || this.entityActions.length > 0 || this.enableImportExport);
    }

    get editParams(): any {
        if (this.stackedPageContextService) {
            return EditParams.isInstance(this.stackedPageContextService.params) ? this.stackedPageContextService.params.params : undefined;
        } else {
            return undefined;
        }
    }

    get actionsTitle(): string {
        if (this.entityActionsComponent && this.entityActionsComponent.first && this.entityActionsComponent.first.title && this.entityActions.length) {
            return this.entityActionsComponent.first.title;
        } else {
            return "wbo.common.label.actions";
        }
    }

    @Input()
    get saveButtonTitle(): string {
        return this._saveButtonTitle;
    }

    set saveButtonTitle(value: string) {
        this._saveButtonTitle = value;
        this.setLabels();
    }

    async initialiseMode(): Promise<void> {
        this.canCreate = await this.entityModel.canCreateEntity();

        if (this.forceEditMode == undefined) {
            if (this.stackedPageContextService) {
                this.loadedIdentifier = (<EditParams>this.stackedPageContextService.params).identifier;
                this.editService.editMode = (<EditParams>this.stackedPageContextService.params).editMode;
                const params: any = (<EditParams>this.stackedPageContextService.params).params;
                if (params) {
                    this.cloningIdentifier = params["clone"];
                }
            } else {
                this.route.params.forEach((params: Params) => {
                    this.loadedIdentifier = params["id"];
                });
                if (this.loadedIdentifier === EditPageComponent.NEW_ENTITY_PARAM) {
                    this.editService.editMode = EditMode.ADD;
                    this.route.queryParams.forEach(params => this.cloningIdentifier = params["clone"]);
                } else {
                    this.editService.editMode = EditMode.EDIT;
                }
            }
        } else {
            this.editService.editMode = this.forceEditMode;
        }
        if (this.editService.editMode === EditMode.EDIT && !await this.entityModel.canUpdateEntity()) {
            this.editService.editMode = EditMode.VIEW;
        }
    }

    ngOnInit(): void {
        this.initialiseEntity();
    }

    ngAfterContentInit(): void {
        this.evaluateEntityActions();
        this.subscriptions.add(this.entityActionsComponent.changes.subscribe(() => this.evaluateEntityActions()));
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.entityActionsSubscription) {
            this.entityActionsSubscription.unsubscribe();
        }
    }

    get descriptionLabel(): string {
        return this.description || ("wbo." + this.entityModel.getResource() + ".edit.description");
    }

    async initialiseEntity(): Promise<void> {
        let load: Promise<any>;

        await this.editMode$;
        this.editService.cloning = false;
        if (this.editService.editMode === EditMode.ADD) {
            this.initialised = true;
            if (this.cloningIdentifier) {
                this.cloneEntity(this.cloningIdentifier, false);
            } else {
                this.entityModel.newEntity();
                this.setLabels();
                this.setupLocalisationForEntity();
            }
        } else {
            const notFoundSubscription = this.errorHandlerService.resourceNotFound$.subscribe((message: DisplayableDataServerMessage) => {
                this.errorHandlerService.displayError("wbo.common.error.notAccessible", message.messageParams);
                notFoundSubscription.unsubscribe();
                this.cancel();
            });
            this.spinnersService.open();
            const start = Date.now();
            load = this.entityModel.loadEntity(this.loadedIdentifier).then(() => {
                this.spinnersService.close();
                this.analyticsService.trackTiming(AnalyticEvent.ENTITY_TIMING_LOAD, (Date.now() - start), this.entityModel);
                notFoundSubscription.unsubscribe();
                this.entityLoaded();
            }).catch((error) => {
                this.spinnersService.close();
                notFoundSubscription.unsubscribe();
                this.errorHandlerService.displayDataClientError(error);
            });
            this.setLabels();
        }
        if (this.stackedPageContextService) {
            setTimeout(() => this.stackedPageContextService.title = this.titleLabel);
        }
        return load;
    }

    setLabels(): void {
        if (this.editService.editMode === EditMode.ADD) {
            this.titleLabel = "wbo." + this.entityModel.getResource() + ".common.add";
            this.saveLabel = this._saveButtonTitle ? this._saveButtonTitle
                : this.shortButtonLabels ? "wbo.common.button.add"
                    : "wbo." + this.entityModel.getResource() + ".common.add";
        } else {
            if (this.editService.editMode === EditMode.VIEW) {
                this.titleLabel = "wbo." + this.entityModel.getResource() + ".common.view";
            } else {
                this.titleLabel = "wbo." + this.entityModel.getResource() + ".common.edit";
            }
            this.saveLabel = this._saveButtonTitle ? this._saveButtonTitle
                : this.shortButtonLabels ? "wbo.common.button.save"
                    : "wbo." + this.entityModel.getResource() + ".common.save";
        }
    }

    setupLocalisationForEntity(): void {
        if (this.entityModel.data instanceof LocalisableEntityData) {
            this.configurationService.getBoolean(Config.LOCALISATION).then((enabled) => {
                if (enabled) {
                    const localisable: LocalisableEntityData<any> = this.entityModel.data as LocalisableEntityData<any>;
                    localisable.localisableContext.universal = this.authService.isUniversal();

                    const outlets: number[] = this.authService.getOutlets();
                    if (outlets) {
                        localisable.localisableContext.outlets = outlets;
                    }

                    const groups: number[] = this.authService.getCustomOutletGroups();
                    if (groups) {
                        localisable.localisableContext.customObjects = groups;
                    }
                }
            });
        }
    }

    entityLoaded(): void {
        this.editService.restrictControls(this.formGroup, true);
        this.initialised = true;

        // set the values with the entity as some controls are lazy loaded and cause required validations to give false reports
        // it is using the proxyTarget so it doesn't lazy load the collections
        // @todo this needs to be done manually as it is triggering lazy load when the data has children
        // this.formGroup.reset(this.model.data.proxyTarget);
    }

    /**
     * Add another entity copying some values from the previous entity
     */
    addAnother(): void {
        const previousData: EntityData<any> = this.entityModel.data;
        this.entityModel.newEntity();
        this.entityFormBuilder.group(this.dataResourceModel); // forces deep initialisation of the entity data
        this.editService.editMode = EditMode.ADD;
        // copy localisation
        if (this.entityModel.data instanceof LocalisableEntityData) {
            this.configurationService.getBoolean(Config.LOCALISATION).then((enabled) => {
                if (enabled) {
                    const localisable: LocalisableEntityData<any> = <LocalisableEntityData<any>>this.entityModel.data;
                    const localisableFrom: LocalisableEntityData<any> = <LocalisableEntityData<any>>previousData;
                    localisable.localisableContext.universal = localisableFrom.localisableContext.universal;
                    if (!localisable.localisableContext.universal) {
                        localisable.localisableContext.accessibleOutlets = localisableFrom.localisableContext.accessibleOutlets;
                        localisable.localisableContext.customObjects = localisableFrom.localisableContext.customObjects;
                        localisable.localisableContext.outlets = localisableFrom.localisableContext.outlets;
                    }
                }
            });
        }
        // emit the event so the fields can be copied
        this.addingAnother.emit(previousData);
        // reset submission and mark controls as untouched and pristine
        this.resetFormState();

        // scroll to the top after adding another entity
        this.scrollToTop();
    }

    cancel(): void {
        if (this.stackedPageContextService) {
            this.closeStackedPage().then((canClose) => {
                if (canClose) {
                    this.stackedPageContextService.cancel();
                }
            });
        } else {
            const confirmation: Promise<Boolean> | boolean = this.beforeCancel();
            if (confirmation instanceof Promise) {
                confirmation.then((confirmed: Boolean) => {
                    if (confirmed) {
                        this.navigateToExit();
                    }
                });
            } else if (confirmation) {
                this.navigateToExit();
            }
        }
    }

    navigateToExit() {
        if (this.exitRoute === EditPageComponent.EXIT_ROUTE_BACK) {
            this.location.back();
        } else if (this.exitRoute !== EditPageComponent.EXIT_ROUTE_STAY) {
            this.router.navigate(["/main/" + (this.exitRoute ? this.exitRoute : (this.entityModel.getResource() + "-search"))]);
        }
    }

    save(): void {
        this.isAddingAnother = this.addAnotherOnSave;
        this.isSaveAndStay = this.editService.editMode === EditMode.ADD && this.stayOnCreate;
        this.performAction();
    }

    saveAndAddAnother(): void {
        this.isAddingAnother = true;
        this.isSaveAndStay = false;
        this.performAction();
    }

    saveAndStay(): void {
        this.isAddingAnother = false;
        this.isSaveAndStay = true;
        this.performAction();
    }

    setReadOnly(): void {
        this.editService.editMode = EditMode.VIEW;
        this.entityModel.readonly = true;
    }

    /**
     * Reload the entity using the last loaded identifier
     */
    async reload(): Promise<void> {
        if (this.loadedIdentifier) {
            return this.initialiseEntity();
        }
    }

    evaluateEntityActions(): void {
        if (this.entityActionsSubscription) {
            this.entityActionsSubscription.unsubscribe();
        }
        if (this.entityActionsComponent.length > 0) {
            this.entityActionsSubscription = this.entityActionsComponent.first.actions$.subscribe((actions) => this.entityActions = actions);
        } else {
            this.entityActions = [];
        }
    }

    clickAction(action: EntityActionComponent): void {
        action.clicked();
    }

    localisationChange(): void {
        this.editService.dirty = true;
    }

    cloneAction(): void {
        if (this.editService.dirty) {
            this.alertService.show({
                title: "wbo.edit.prompt.cloningSaveChanges.heading",
                message: "wbo.edit.prompt.cloningSaveChanges.message",
                params: {},
                type: FUIAlertType.CONFIRMATION,
                actions: [
                    FUIAlertActions.CANCEL_ACTION,
                    {
                        name: "discard",
                        label: "wbo.edit.button.discard"
                    }, {
                        name: "save",
                        label: "wbo.edit.button.save"
                    }
                ]
            }).subscribe((action) => {
                if (action.name === "save") {
                    this.savingAndCloning = true;
                    this.save();
                } else if (action.name === "discard") {
                    this.cloneEntity(this.loadedIdentifier, true);
                }
            });
        } else {
            this.cloneEntity(this.loadedIdentifier, true);
        }
    }

    cloneEntity(identifier: string, redirect: boolean): void {
        if (this.stackedPageContextService && redirect) {
            this.stackedPageService.open(this.routingDiscoveryService.findRoutingComponent(this.entityModel.getResource()),
                new EditParams(this.loadedIdentifier, EditMode.ADD, undefined, {clone: this.loadedIdentifier}));
        } else {
            this.spinnersService.open();
            this.editService.cloning = true;
            this.cloningIdentifier = identifier;
            this.entityModel.cloneEntity(identifier).then(() => {
                this.spinnersService.close();
                this.editService.editMode = EditMode.ADD;
                this.setLabels();
                this.resetFormState();
                if (redirect) {
                    this.router.navigate(["/main/" + this.entityModel.getResource() + "/" + EditPageComponent.NEW_ENTITY_PARAM],
                        {queryParams: {clone: this.loadedIdentifier}});
                }
                this.loadedIdentifier = undefined;
                this.editService.restrictControls(this.formGroup);
            }).catch((error) => {
                this.cloningIdentifier = undefined;
                this.editService.cloning = false;
                this.spinnersService.close();
                this.errorHandlerService.displayDataClientError(error);
            });
        }
    }

    protected override beforeExecution(): Promise<Boolean> | boolean {
        const actionEvent: EditPageActionEvent = new EditPageActionEvent(this.editService.editMode);
        this.saving.emit(actionEvent);
        if (actionEvent.actionComplete) {
            return actionEvent.actionComplete;
        } else {
            return !actionEvent.isDefaultPrevented();
        }
    }

    protected beforeCancel(): Promise<Boolean> | boolean {
        const actionEvent: EditPageActionEvent = new EditPageActionEvent(this.editService.editMode);
        this.cancelling.emit(actionEvent);
        if (actionEvent.actionComplete) {
            return actionEvent.actionComplete;
        } else {
            return !actionEvent.isDefaultPrevented();
        }
    }

    protected execute(): Promise<Boolean> {
        const start = Date.now();
        this.savedData = this.entityModel.data;
        return this.entityModel.saveEntity()
            .then((validationMessagesEntity: ValidationMessage[]) => {
                if (validationMessagesEntity) {
                    const actionEvent: EditPageActionEvent = new EditPageActionEvent(this.editService.editMode, this.entityModel.data);
                    this.saveFailed.emit(actionEvent);
                    this.handleValidationMessages(validationMessagesEntity);
                    return false;
                }

                if (this.editService.editMode === EditMode.ADD) {
                    this.analyticsService.trackEvent(AnalyticEvent.ENTITY_ADD, this.entityModel);
                    this.analyticsService.trackTiming(AnalyticEvent.ENTITY_TIMING_ADD, (Date.now() - start), this.entityModel);
                } else if (this.editService.editMode === EditMode.EDIT) {
                    this.analyticsService.trackEvent(AnalyticEvent.ENTITY_EDIT, this.entityModel);
                    this.analyticsService.trackTiming(AnalyticEvent.ENTITY_TIMING_EDIT, (Date.now() - start), this.entityModel);
                }

                // set the version from the server so when the stackedPageContextService confirms the entity it can be saved again
                this.savedData["version"] = this.entityModel.data.version;

                const actionEvent: EditPageActionEvent = new EditPageActionEvent(this.editService.editMode, this.entityModel.data);
                this.saved.emit(actionEvent);
                if (actionEvent.actionComplete) {
                    return actionEvent.actionComplete.then((complete: Boolean) => {
                        if (complete) {
                            this.executionComplete();
                        }
                        return complete;
                    });
                }

                if (!actionEvent.isDefaultPrevented()) {
                    this.executionComplete();
                }
                return !actionEvent.isDefaultPrevented();
            })
            .catch((reason: any) => {
                const actionEvent: EditPageActionEvent = new EditPageActionEvent(this.editService.editMode, this.entityModel.data);
                this.saveFailed.emit(actionEvent);
                throw reason;
            });
    }

    private executionComplete() {
        this.editService.dirty = false;
        if (this.stackedPageContextService) {
            this.stackedPageContextService.confirm(this.editService.editMode === EditMode.ADD ? this.entityModel.data : this.savedData);
        } else if (this.savingAndCloning) {
            this.cloneEntity(this.loadedIdentifier, true);
            this.savingAndCloning = false;
        } else if (this.isAddingAnother) {
            this.addAnother();
        } else if (this.isSaveAndStay) {
            this.resetFormState();
            if (this.editService.editMode === EditMode.ADD) {
                this.editService.editMode = EditMode.EDIT;
                this.entityLoaded();
                this.setLabels();
                this.loadedIdentifier = this.entityModel.data.getIdentifier();
            }
        } else {
            this.navigateToExit();
        }
    }

    public resetFormState(): void {
        this.editService.resetSubmission();
        this.markControlsAsUntouchedAndPristine(this.formGroup);
        this.editService.dirty = false;
    }

    private configureForScreenSize(): void {
        if (this.responsiveService.breakpointApplies(FUIBreakpoints.mediumDown)) {
            this.shortButtonLabels = true;

            if (this.responsiveService.breakpointApplies(FUIBreakpoints.smallDown)) {
                this.hideAddAnotherButton = true;
            }
        } else {
            this.shortButtonLabels = false;
            this.hideAddAnotherButton = false;
        }

        this.setLabels();
    }

    exportEntity(): void {
        const blob: Blob = new Blob([this.entityModel.data.toJSONString()], {type: "application/json"});
        const fileName: string = this.title ? this.title.replace(/([^a-z0-9\s]+)/gi, "") : "entity";
        FileSaver.saveAs(blob, fileName + ".json");
    }

    importEntity(): void {
        this.modalsService.create(FileUploadComponent).subscribe((modal: FileUploadComponent) => {
            modal.onConfirm.subscribe((fileContent) => {
                const importObject: any = JSON.parse(fileContent);
                delete importObject["localisableContext"];
                this.deletePropertyRecursively([], importObject);

                if (this.ignoreFieldsImportExport) {
                    this.ignoreFieldsImportExport.forEach((ignoreField) => delete importObject[ignoreField]);
                }
                for (const key in importObject) {
                    this.entityModel.data[key] = importObject[key];
                }
                this.entityModel.notifyDataChange();
                this.entityImported.emit();
                this.changeDetectorRef.detectChanges();
            });
        });
    }

    deletePropertyRecursively(path: string[], importingEntity: any): void {
        delete importingEntity["version"];

        if (path.length == 0) {
            delete importingEntity[this.entityModel.data.getIdentifierKey()];
        } else {
            const entityDataType: typeof EntityData = this.entityModel.data.getEntityDataType(path.join("."));
            if (entityDataType) {
                const identifierKey: string = new entityDataType().getIdentifierKey();
                if (identifierKey) {
                    delete importingEntity[identifierKey];
                }
            }
        }

        for (const key in importingEntity) {
            const value: any = importingEntity[key];
            if (value != undefined) {
                if (Array.isArray(value)) {
                    value.forEach((entity) => {
                        this.deletePropertyRecursively([...path, key], entity);
                    });
                } else if (typeof value === "object") {
                    this.deletePropertyRecursively([...path, key], value);
                }
            }
        }
    }

    public createModal<T>(modalComponent: Type<T>): Observable<T> {
        return this.modalsService.create(modalComponent, this.injector);
    }

    public destroyModal(modal: any): Observable<void> {
        return this.modalsService.destroy(modal);
    }

    override shouldCheckDiscardChanges(): boolean {
        return !this.shouldDiscardChanges;
    }
}
